import React from 'react';
import PropTypes from 'prop-types';
import { Flyout } from '@els/els-react--flyout';
import InnerHTML from '@Components/InnerHTML';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import ImageRequest from '@Containers/ImageRequest';
import * as FormatUtils from '@Services/FormatUtils';
import getHoverFlyoutModifiers from '@Utils/Flyout';

import './StructurePreview.scss';

const getImageAltText = (chemicalNames, title, imageLabel) => `Chemical structure of ${chemicalNames?.[0] || title || imageLabel}`;

const getTitleFlyoutTriggerAttrs = (hasTitleFlyout, imageAltText) =>
  hasTitleFlyout
    ? {
        role: 'img',
        'aria-label': imageAltText,
        tabIndex: 0,
      }
    : {};

const renderStructureImage = ({ src, alt, ariaHidden, loading }) => (
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
  <img src={src} alt={alt} aria-hidden={ariaHidden} loading={loading} draggable="false" onDragStart={(e) => e.preventDefault()} />
);

function StructurePreview({
  title = '',
  structureId = '',
  mixtureName = null,
  structureName = null,
  previewPathKey,
  previewPath,
  zoom = 0,
  markushPreview,
  chemicalNames,
}) {
  const customAttrs = title ? { title } : {};
  const hasTitleFlyout = Boolean(title);

  const getNoImageComponent = () => (
    <div className="rx-picture no-image" {...customAttrs}>
      <div className="no-image-content">
        <div>No Structure Preview</div>
        {structureId && <div>RN: {structureId}</div>}
        {structureName && <div>{structureName}</div>}
      </div>
    </div>
  );

  const getMixtureNameComponent = () => (
    <div className="mixture-name-wrapper">
      <div className="mixture-name">{(mixtureName || []).join('; ')}</div>
    </div>
  );

  const getRegularPreviewComponent = () => {
    const altText = title ? <span className="screen-reader-text">{title}</span> : null;
    const previewUrl = previewPath?.[previewPathKey]?.url;
    const url = previewUrl && FormatUtils.formatPath(previewUrl);
    const image =
      previewPathKey && previewPath?.[previewPathKey]
        ? {
            ...previewPath[previewPathKey],
            url,
          }
        : FormatUtils.getImageFromPathForZoom(previewPath, zoom, true) || {};

    if (image.url) {
      const imageGenerationRequestsLimit = Auth.isFeatureEnabled(FEATURES.image_generation_requests_limit);
      const imageAltText = getImageAltText(chemicalNames, title, image.label);
      const imageAlt = hasTitleFlyout ? '' : imageAltText;
      const imageAriaHidden = hasTitleFlyout ? 'true' : undefined;
      const previewComponent = (
        <div
          className={`structure-preview restrict-select${hasTitleFlyout ? ' structure-preview-flyout-trigger rx-focusable' : ''}`}
          {...getTitleFlyoutTriggerAttrs(hasTitleFlyout, imageAltText)}>
          {imageGenerationRequestsLimit ? (
            <ImageRequest
              url={image.url}
              limit={parseInt(imageGenerationRequestsLimit, 10)}
              render={({ src }) => renderStructureImage({ src, alt: imageAlt, ariaHidden: imageAriaHidden, loading: 'lazy' })}
            />
          ) : (
            renderStructureImage({ src: image.url, alt: imageAlt, ariaHidden: imageAriaHidden })
          )}
        </div>
      );

      return hasTitleFlyout ? (
        <Flyout
          className="structure-preview-flyout"
          flyout={<span aria-hidden="true">{title}</span>}
          trigger="hover"
          placement="top-start"
          width="max-content"
          theme="simple"
          pointerType="primary"
          modifiers={getHoverFlyoutModifiers()}
          shouldCloseOnESC>
          {previewComponent}
        </Flyout>
      ) : (
        previewComponent
      );
    }

    return (
      <div className="structure-preview" {...customAttrs}>
        <InnerHTML tag="div" className="u-els-color-n6 u-els-font-family-bold no-image-structure" title={image.label} html={image.label} />
        {altText}
      </div>
    );
  };

  const getMarkushPreviewComponent = () => {
    const path = FormatUtils.formatPath(markushPreview.url);
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    return <img src={path} alt={structureId || ''} draggable="false" onDragStart={(e) => e.preventDefault()} />;
  };

  if (markushPreview) return getMarkushPreviewComponent();
  if (!previewPath) return getNoImageComponent();
  if (mixtureName) return getMixtureNameComponent();
  return getRegularPreviewComponent();
}

StructurePreview.propTypes = {
  title: PropTypes.string,
  structureId: PropTypes.string,
  mixtureName: PropTypes.array,
  structureName: PropTypes.string,
  previewPathKey: PropTypes.string,
  previewPath: PropTypes.object,
  zoom: PropTypes.number,
  markushPreview: PropTypes.object,
  chemicalNames: PropTypes.array,
};

export default StructurePreview;
